(function () {
  const SELECTOR = 'lb-lens-builder';
  function parseJson(root, selector, fallback) {
    try {
      return JSON.parse(root.querySelector(selector)?.textContent || '');
    } catch (error) {
      return fallback;
    }
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    });
  }

  function cents(value) {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return Math.round(Number(value) * 100);
    return 0;
  }

  function optionValuesForVariant(variant) {
    if (!variant) return [];
    if (Array.isArray(variant.options)) return variant.options;
    return [variant.option1, variant.option2, variant.option3].filter(Boolean);
  }

  function normalize(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  async function readJsonResponse(response, fallbackMessage) {
    const text = await response.text();

    try {
      return JSON.parse(text);
    } catch (error) {
      if (response.status === 429) {
        throw new Error('Shopify is verifying this session. Wait a moment and try again.');
      }

      throw new Error(fallbackMessage || 'Shopify returned an unreadable cart response.');
    }
  }

  function fixedStep(value) {
    const number = Number(value);
    if (Number.isInteger(number)) return String(number);
    return number.toFixed(2);
  }

  function fixedTwo(value) {
    return Number(value).toFixed(2);
  }

  function prefixedPlus(value) {
    const number = Number(value);
    return `${number > 0 ? '+' : ''}${number.toFixed(2)}`;
  }

  function getCheckedValue(root, name) {
    return root.querySelector(`input[name="${CSS.escape(name)}"]:checked`)?.value || '';
  }

  function setError(root, key, message) {
    const element = root.querySelector(`[data-lb-error-for="${key}"]`);
    if (!element) return;
    element.textContent = message || '';
    element.hidden = !message;
  }

  function clearErrors(root) {
    root.querySelectorAll('[data-lb-error-for]').forEach((element) => {
      element.textContent = '';
      element.hidden = true;
    });
  }

  class LensBuilder {
    constructor(root) {
      this.root = root;
      this.product = parseJson(root, '[data-lb-product]', {});
      this.config = parseJson(root, '[data-lb-config]', {});
      this.formId = root.dataset.formId;
      this.primaryLensValue = root.dataset.primaryLensValue || 'Blue Light Glasses';
      this.rxLensValue = root.dataset.rxLensValue || 'Readers and Prescription';
      this.productId = String(root.dataset.productId || this.product.id || '');
      this.storageKey = `lb-lens-mode:${this.productId}`;
      this.productOptions = this.product.options || [];
      this.lensOptionIndex = this.productOptions.indexOf('Lenses');
      this.variants = this.product.variants || [];
      this.form = document.getElementById(this.formId);
      this.productInfo = this.form?.closest('.product-info') || root.closest('.product-info');
      this.dockFooterVisible = false;
      this.dockFooterObserver = null;

      if (!this.form || this.lensOptionIndex === -1) return;

      this.root.lbController = this;

      this.populateSelects();
      this.markNativeLensOption();
      this.bindEvents();
      this.setupSummaryDock();
      this.mode = this.initialMode();
      this.applyMode(this.mode, { syncNative: false });
      this.update();
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const quantityButton = event.target.closest('[data-lb-quantity-button]');
        if (quantityButton && this.root.contains(quantityButton)) {
          window.setTimeout(() => this.update(), 0);
          return;
        }

        const toggle = event.target.closest('[data-lb-help-toggle]');
        if (!toggle || !this.root.contains(toggle)) return;

        event.preventDefault();
        event.stopPropagation();
        this.toggleHelp(toggle);
      });

      this.root.addEventListener('change', (event) => {
        if (event.target.matches('[data-lb-field="mode"] input, [data-lb-mode-choice] input')) {
          this.applyMode(event.target.value, { syncNative: true, updateUrl: true });
          return;
        }

        if (event.target.matches(`input[name="lb-rx-method-${this.productId}"]`)) {
          clearErrors(this.root);
          const status = this.root.querySelector('[data-lb-status]');
          if (status) status.innerHTML = '';
        }

        if (event.target.matches('[data-lb-upload-file]') && event.target.files?.length) {
          setError(this.root, 'upload', '');
        }

        if (event.target.matches('input, select, textarea')) {
          this.update();
        }
      });

      this.root.querySelector('[data-lb-add]')?.addEventListener('click', () => this.addConfiguredPair());
      this.root.querySelector('[data-lb-dock-add]')?.addEventListener('click', () => this.addConfiguredPair());

      this.form.addEventListener('variant:change', () => {
        window.setTimeout(() => this.update(), 0);
      });
    }

    toggleHelp(toggle) {
      const targetId = toggle.getAttribute('aria-controls');
      if (!targetId) return;

      const help = this.root.querySelector(`#${CSS.escape(targetId)}`);
      if (!help) return;

      const isOpening = toggle.getAttribute('aria-expanded') !== 'true';
      if (isOpening) {
        this.root.querySelectorAll('[data-lb-help-toggle][aria-expanded="true"]').forEach((openToggle) => {
          if (openToggle === toggle) return;
          this.closeHelp(openToggle);
        });
      }

      toggle.setAttribute('aria-expanded', String(isOpening));
      help.hidden = !isOpening;
    }

    closeHelp(toggle) {
      toggle.setAttribute('aria-expanded', 'false');

      const targetId = toggle.getAttribute('aria-controls');
      if (!targetId) return;

      const help = this.root.querySelector(`#${CSS.escape(targetId)}`);
      if (help) help.hidden = true;
    }

    populateSelects() {
      const readerConfig = this.config.readerStrength || { min: 0.25, max: 5, step: 0.25 };
      this.populateReaderStrengthButtons(readerConfig);

      const rx = this.config.manualRx || {};
      this.root.querySelectorAll('[data-lb-rx-field$="_sph"]').forEach((select) => {
        this.populateRange(select, rx.sph || { min: -20, max: 12, step: 0.25 }, prefixedPlus, true);
      });
      this.root.querySelectorAll('[data-lb-rx-field$="_cyl"]').forEach((select) => {
        this.populateRange(select, rx.cyl || { min: -6, max: 6, step: 0.25 }, prefixedPlus, true, '0.00');
      });
      this.root.querySelectorAll('[data-lb-rx-field$="_axis"]').forEach((select) => {
        this.populateRange(select, rx.axis || { min: 0, max: 180, step: 1 }, fixedStep, true);
      });
      this.root.querySelectorAll('[data-lb-rx-field$="_add"]').forEach((select) => {
        this.populateRange(select, rx.add || { min: 1, max: 6, step: 0.25 }, prefixedPlus, true);
      });
      this.populateRange(this.root.querySelector('[data-lb-rx-field="pd"]'), rx.pd || { min: 50, max: 76, step: 0.5 }, fixedTwo, true);
    }

    populateReaderStrengthButtons(range) {
      const grid = this.root.querySelector('[data-lb-reader-strength-grid]');
      if (!grid || grid.dataset.lbPopulated === 'true') return;
      grid.dataset.lbPopulated = 'true';

      const min = Number(range.min);
      const max = Number(range.max);
      const step = Number(range.step);
      const precision = String(step).includes('.') ? String(step).split('.')[1].length : 0;
      const name = `lb-reader-strength-${this.productId}`;

      for (let value = min; value <= max + step / 10; value += step) {
        const rounded = Number(value.toFixed(precision));
        const inputValue = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
        const label = document.createElement('label');
        label.className = 'lb-lens-choice lb-lens-choice--strength block-swatch';

        const input = document.createElement('input');
        input.type = 'radio';
        input.name = name;
        input.value = inputValue;
        input.dataset.lbReaderStrength = 'true';

        const text = document.createElement('span');
        text.textContent = prefixedPlus(rounded);

        label.append(input, text);
        grid.append(label);
      }
    }

    populateRange(select, range, formatter, includeEmpty, selectedValue) {
      if (!select || select.dataset.lbPopulated === 'true') return;
      select.dataset.lbPopulated = 'true';

      if (includeEmpty) {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = '-';
        select.append(option);
      }

      const min = Number(range.min);
      const max = Number(range.max);
      const step = Number(range.step);
      const precision = String(step).includes('.') ? String(step).split('.')[1].length : 0;

      for (let value = min; value <= max + step / 10; value += step) {
        const rounded = Number(value.toFixed(precision));
        const option = document.createElement('option');
        option.value = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
        option.textContent = formatter(rounded);
        if (selectedValue && option.value === selectedValue) option.selected = true;
        select.append(option);
      }
    }

    initialMode() {
      const requestedMode = new URL(window.location.href).searchParams.get('lens');
      if (['primary', 'readers', 'prescription'].includes(requestedMode)) return requestedMode;

      const storedMode = window.sessionStorage?.getItem(this.storageKey);
      if (['primary', 'readers', 'prescription'].includes(storedMode)) return storedMode;

      const selectedVariant = this.currentVariant();
      const selectedLens = optionValuesForVariant(selectedVariant)[this.lensOptionIndex];
      return selectedLens === this.rxLensValue ? 'readers' : 'primary';
    }

    syncModeUrl(mode) {
      const url = new URL(window.location.href);
      url.searchParams.set('lens', mode);
      window.history.replaceState({ path: url.toString() }, '', url.toString());
    }

    applyMode(mode, options = {}) {
      if (!['primary', 'readers', 'prescription'].includes(mode)) mode = 'primary';
      this.mode = mode;
      window.sessionStorage?.setItem(this.storageKey, mode);

      this.root.querySelectorAll('[data-lb-mode-choice]').forEach((label) => {
        const input = label.querySelector('input');
        const checked = input.value === mode;
        input.checked = checked;
        label.classList.toggle('is-selected', checked);
      });

      this.root.querySelector('[data-lb-panel="readers"]').hidden = mode !== 'readers';
      this.root.querySelector('[data-lb-panel="prescription"]').hidden = mode !== 'prescription';
      this.root.toggleAttribute('data-lb-custom-mode', mode !== 'primary');
      if (!this.root.closest('lb-build-pair')) {
        this.productInfo?.classList.toggle('lb-lens-builder-mode-active', mode !== 'primary');
      }
      this.toggleThemeBuyButtons(mode === 'primary');

      if (options.updateUrl) this.syncModeUrl(mode);

      if (options.syncNative) {
        const targetLensValue = mode === 'primary' ? this.primaryLensValue : this.rxLensValue;
        this.syncNativeLensValue(targetLensValue);
      }

      this.update();
    }

    toggleThemeBuyButtons(show) {
      if (this.root.closest('lb-build-pair')) return;

      const productInfo = this.productInfo || this.form?.closest('.product-info');
      productInfo?.querySelectorAll('.product-info__block-item[data-block-type="buy-buttons"]').forEach((block) => {
        block.hidden = !show;
      });
      productInfo?.querySelectorAll('.product-info__block-item[data-block-type="quantity-selector"]').forEach((block) => {
        block.hidden = !show;
      });

      document.querySelectorAll(`product-sticky-bar[form="${CSS.escape(this.formId)}"]`).forEach((bar) => {
        bar.hidden = !show;
      });
    }

    nativeLensFieldset() {
      const picker = this.form.closest('.product-info')?.querySelector(`variant-picker[form-id="${CSS.escape(this.formId)}"]`);
      if (!picker) return null;

      return Array.from(picker.querySelectorAll('fieldset')).find((fieldset) => {
        const legend = normalize(fieldset.querySelector('legend')?.textContent).replace(/:$/, '');
        return legend === 'Lenses';
      }) || null;
    }

    markNativeLensOption() {
      if (this.root.closest('lb-build-pair')) return;
      this.nativeLensFieldset()?.classList.add('lb-native-lens-option');
    }

    syncNativeLensValue(labelText) {
      if (this.root.closest('lb-build-pair')) return;
      const fieldset = this.nativeLensFieldset();
      if (!fieldset) return;
      fieldset.classList.add('lb-native-lens-option');

      const target = Array.from(fieldset.querySelectorAll('label')).find((label) => {
        return normalize(label.textContent).includes(labelText);
      });
      const input = target ? fieldset.querySelector(`#${CSS.escape(target.getAttribute('for'))}`) : null;

      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    currentVariant() {
      const id = Number(this.form?.elements.id?.value || 0);
      return this.variants.find((variant) => Number(variant.id) === id) || null;
    }

    selectedOptionMap() {
      const selected = {};
      const picker = this.form.closest('.product-info')?.querySelector(`variant-picker[form-id="${CSS.escape(this.formId)}"]`);

      picker?.querySelectorAll('fieldset').forEach((fieldset) => {
        const name = normalize(fieldset.querySelector('legend')?.textContent).replace(/:$/, '');
        const checked = fieldset.querySelector('input[data-option-position]:checked');
        if (!name || !checked) return;

        const label = fieldset.querySelector(`label[for="${CSS.escape(checked.id)}"]`);
        selected[name] = normalize(label?.textContent);
      });

      const current = this.currentVariant();
      const values = optionValuesForVariant(current);
      this.productOptions.forEach((name, index) => {
        if (!selected[name] && values[index]) selected[name] = values[index];
      });

      return selected;
    }

    findVariantForLens(lensValue) {
      const selected = this.selectedOptionMap();

      return this.variants.find((variant) => {
        const values = optionValuesForVariant(variant);
        if (values[this.lensOptionIndex] !== lensValue) return false;

        return this.productOptions.every((optionName, index) => {
          if (index === this.lensOptionIndex) return true;
          return !selected[optionName] || values[index] === selected[optionName];
        });
      }) || null;
    }

    currentFrameVariantForMode() {
      const lensValue = this.mode === 'primary' ? this.primaryLensValue : this.rxLensValue;
      return this.findVariantForLens(lensValue);
    }

    update() {
      this.markNativeLensOption();
      this.updateModeAvailability();
      this.updateReaderVisibility();
      this.updatePrescriptionVisibility();
      this.updateFooterVisibility();
      this.updateChoiceClasses();
      this.updateTotals();
      this.updateSummaryDock();
    }

    updateModeAvailability() {
      const rxVariant = this.findVariantForLens(this.rxLensValue);
      const rxAvailable = Boolean(rxVariant && rxVariant.available);
      const primaryVariant = this.findVariantForLens(this.primaryLensValue);
      const primaryAvailable = Boolean(primaryVariant && primaryVariant.available);
      const availability = this.root.querySelector('[data-lb-availability]');

      this.root.querySelectorAll('[data-lb-mode-choice]').forEach((label) => {
        const input = label.querySelector('input');
        const isPrimary = input.value === 'primary';
        const disabled = isPrimary ? !primaryAvailable : !rxAvailable;
        input.disabled = disabled;
        label.classList.toggle('is-disabled', disabled);
      });

      if (!rxAvailable && this.mode !== 'primary') {
        this.applyMode('primary', { syncNative: true, updateUrl: true });
        return;
      }

      if (availability) {
        availability.hidden = rxAvailable;
        availability.textContent = rxAvailable ? '' : 'Readers and prescription are not available for this selected color.';
      }

      const addButton = this.root.querySelector('[data-lb-add]');
      if (addButton) addButton.disabled = this.mode !== 'primary' && !rxAvailable;
    }

    updateReaderVisibility() {
      this.root.querySelectorAll('[data-lb-reader-strength]').forEach((input) => {
        input.disabled = this.mode !== 'readers';
      });
    }

    updatePrescriptionVisibility() {
      const rxType = getCheckedValue(this.root, `lb-rx-type-${this.productId}`);
      const method = getCheckedValue(this.root, `lb-rx-method-${this.productId}`);
      const hasRxType = Boolean(rxType);
      const hasMethod = Boolean(method);
      const isProgressive = rxType === 'progressives';
      const isManual = method === 'manual';
      const isUpload = method === 'upload';
      const methodNeedsPath = isManual || isUpload;
      const manualFields = this.root.querySelector('[data-lb-manual-fields]');
      const blueStep = this.root.querySelector('[data-lb-rx-step="blue"]');
      const methodStep = this.root.querySelector('[data-lb-rx-step="method"]');
      const pathStep = this.root.querySelector('[data-lb-rx-step="path"]');
      const lensStep = this.root.querySelector('[data-lb-rx-step="lens"]');

      if (blueStep) blueStep.hidden = this.mode !== 'prescription' || !hasRxType;
      if (methodStep) methodStep.hidden = this.mode !== 'prescription' || !hasRxType;
      if (pathStep) pathStep.hidden = this.mode !== 'prescription' || !hasRxType || !hasMethod || !methodNeedsPath;
      if (lensStep) lensStep.hidden = this.mode !== 'prescription' || !hasRxType || !hasMethod;

      this.root.querySelector('[data-lb-upload-fields]').hidden = !hasRxType || !hasMethod || !isUpload;
      manualFields.hidden = !hasRxType || !hasMethod || !isManual;
      manualFields.classList.toggle('is-progressive', isProgressive);
      this.root.querySelectorAll('[data-lb-add-head], [data-lb-add-field]').forEach((element) => {
        element.hidden = !isProgressive || !isManual || !hasMethod;
      });
    }

    updateFooterVisibility() {
      const footer = this.root.querySelector('[data-lb-custom-footer]');
      if (!footer) return;

      footer.hidden = !this.priceCanDisplay();
    }

    setupSummaryDock() {
      const footer = this.root.querySelector('[data-lb-custom-footer]');
      if (!footer || !('IntersectionObserver' in window)) return;

      this.dockFooterObserver = new IntersectionObserver((entries) => {
        this.dockFooterVisible = Boolean(entries[0]?.isIntersecting);
        this.updateSummaryDock();
      }, { threshold: 0.15 });
      this.dockFooterObserver.observe(footer);
    }

    summaryProgressText() {
      if (this.mode === 'readers') {
        const strength = getCheckedValue(this.root, `lb-reader-strength-${this.productId}`);
        return strength ? `${prefixedPlus(strength)} strength selected` : 'Choose a strength';
      }

      if (this.mode === 'prescription') {
        const type = getCheckedValue(this.root, `lb-rx-type-${this.productId}`);
        const method = getCheckedValue(this.root, `lb-rx-method-${this.productId}`);

        if (!type) return 'Choose prescription type';
        if (!method) return 'Choose how to send it';
        if (this.uploadWaitingForFile()) return 'Upload your prescription';
        return 'Your lens choices are ready';
      }

      return '';
    }

    updateSummaryDock() {
      const dock = this.root.querySelector('[data-lb-summary-dock]');
      if (!dock) return;

      const showDock = this.mode !== 'primary' && !this.dockFooterVisible;
      dock.hidden = !showDock;
      if (!showDock) return;

      const mode = dock.querySelector('[data-lb-dock-mode]');
      const progress = dock.querySelector('[data-lb-dock-progress]');
      const dockTotal = dock.querySelector('[data-lb-dock-total]');
      const total = normalize(this.root.querySelector('[data-lb-total]')?.textContent);
      const addButton = this.root.querySelector('[data-lb-add]');
      const dockButton = dock.querySelector('[data-lb-dock-add]');

      if (mode) mode.textContent = this.mode === 'readers' ? 'Readers' : 'Prescription';
      if (progress) progress.textContent = this.summaryProgressText();
      if (dockTotal) dockTotal.textContent = total || 'Price shown when ready';

      if (addButton && dockButton) {
        dockButton.disabled = addButton.disabled;
        dockButton.textContent = addButton.textContent;
        dockButton.toggleAttribute('aria-busy', addButton.getAttribute('aria-busy') === 'true');
      }
    }

    updateChoiceClasses() {
      this.root.querySelectorAll('.lb-lens-choice').forEach((label) => {
        const input = label.querySelector('input');
        if (!input) return;
        label.classList.toggle('is-selected', input.checked);
        label.classList.toggle('is-disabled', input.disabled);
      });
    }

    selectedReaderLensVariant() {
      const blue = getCheckedValue(this.root, `lb-reader-blue-${this.productId}`) || 'None';
      const mag = getCheckedValue(this.root, `lb-reader-mag-${this.productId}`) || 'Standard Magnification';
      const variant = (this.config.lensVariants?.readers || {})[`${blue}|${mag}`];
      return variant?.available === false ? null : variant;
    }

    selectedPrescriptionLensVariant() {
      const type = getCheckedValue(this.root, `lb-rx-type-${this.productId}`) || 'singleVision';
      const blue = getCheckedValue(this.root, `lb-rx-blue-${this.productId}`) || 'None';
      const index = getCheckedValue(this.root, `lb-rx-index-${this.productId}`) || '1.56 High Index';
      const group = type === 'progressives'
        ? this.config.lensVariants?.progressives || {}
        : this.config.lensVariants?.singleVision || {};
      const variant = group[`${blue}|${index}`];
      return variant?.available === false ? null : variant;
    }

    selectedLensVariant() {
      return this.mode === 'readers' ? this.selectedReaderLensVariant() : this.selectedPrescriptionLensVariant();
    }

    updateTotals() {
      const total = this.root.querySelector('[data-lb-total]');
      if (!total || this.mode === 'primary') return;

      const frameVariant = this.currentFrameVariantForMode();
      const lensVariant = this.selectedLensVariant();
      const quantity = this.quantity();

      if (!this.priceCanDisplay()) {
        total.textContent = '';
        this.updateAddButton(false);
        return;
      }

      if (!frameVariant || frameVariant.available === false || !lensVariant) {
        total.textContent = 'Unavailable';
        this.updateAddButton(false);
        return;
      }

      this.updatePriceClass(total);
      this.updateAddButton(true);

      const pairTotal = cents(frameVariant?.price) + cents(lensVariant?.price);

      total.textContent = quantity > 1
        ? `${formatMoney(pairTotal)} each / ${formatMoney(pairTotal * quantity)} total`
        : formatMoney(pairTotal);
    }

    updatePriceClass(total) {
      total.classList.remove('text-on-sale');
      total.classList.add('text-subdued');
    }

    updateAddButton(isAvailable) {
      const addButtons = this.root.querySelectorAll('[data-lb-add], [data-lb-dock-add]');
      if (!addButtons.length) return;

      const state = this.addButtonState(isAvailable);
      addButtons.forEach((addButton) => {
        addButton.disabled = state.disabled;
        addButton.textContent = state.text;
      });
    }

    addButtonState(isAvailable) {
      if (!isAvailable) return { disabled: true, text: 'ADD TO CART' };

      if (this.mode === 'readers' && !this.readerStrengthSelected()) {
        return { disabled: true, text: 'CHOOSE STRENGTH' };
      }

      if (this.mode === 'prescription' && !this.prescriptionSelectionsReady()) {
        return { disabled: true, text: 'CHOOSE METHOD' };
      }

      if (this.uploadWaitingForFile()) {
        return { disabled: true, text: 'UPLOAD PRESCRIPTION' };
      }

      return { disabled: false, text: 'ADD TO CART' };
    }

    quantity() {
      if (this.mode !== 'primary') {
        const customQuantityInput = this.root.querySelector('[data-lb-quantity-input]');
        if (customQuantityInput) return Math.max(1, Number(customQuantityInput.value || 1));
      }

      const quantityInput = this.form.querySelector('input[name="quantity"]');
      return Math.max(1, Number(quantityInput?.value || 1));
    }

    uploadWaitingForFile() {
      const method = getCheckedValue(this.root, `lb-rx-method-${this.productId}`);
      return this.mode === 'prescription'
        && method === 'upload'
        && !this.root.querySelector('[data-lb-upload-file]')?.files?.length;
    }

    priceCanDisplay() {
      if (this.mode === 'readers') return true;
      if (this.mode === 'prescription') return true;
      return false;
    }

    readerStrengthSelected() {
      return Boolean(getCheckedValue(this.root, `lb-reader-strength-${this.productId}`));
    }

    prescriptionSelectionsReady() {
      const rxType = getCheckedValue(this.root, `lb-rx-type-${this.productId}`);
      const method = getCheckedValue(this.root, `lb-rx-method-${this.productId}`);
      return Boolean(rxType && method);
    }

    customFlowReady() {
      if (this.mode === 'readers') return this.readerStrengthSelected();
      if (this.mode === 'prescription') return this.prescriptionSelectionsReady();
      return false;
    }

    validate() {
      clearErrors(this.root);
      const errors = [];
      const frameVariant = this.currentFrameVariantForMode();
      const lensVariant = this.selectedLensVariant();

      if (!frameVariant || frameVariant.available === false) {
        errors.push(['readerStrength', 'This lens option is not available for the selected color.']);
      }

      if (this.mode !== 'primary' && !lensVariant) {
        errors.push([this.mode === 'readers' ? 'readerStrength' : 'manual', 'This lens add-on is not available right now.']);
      }

      if (this.mode === 'readers') {
        if (!this.readerStrengthSelected()) {
          errors.push(['readerStrength', 'Choose a reader strength.']);
        }
      }

      if (this.mode === 'prescription') {
        const rxType = getCheckedValue(this.root, `lb-rx-type-${this.productId}`);
        const method = getCheckedValue(this.root, `lb-rx-method-${this.productId}`);

        if (!rxType) {
          errors.push(['prescriptionType', 'Choose a prescription type.']);
        }

        if (!method) {
          errors.push(['prescriptionMethod', 'Choose how you want to send your prescription.']);
        }

        if (method === 'upload' && !this.root.querySelector('[data-lb-upload-file]')?.files?.length) {
          errors.push(['upload', 'Upload your prescription file or choose another method.']);
        }

        if (method === 'manual') {
          const manualErrors = this.validateManualRx();
          if (manualErrors.length) errors.push(['manual', manualErrors.join(' ')]);
        }
      }

      errors.forEach(([key, message]) => setError(this.root, key, message));
      return errors.length === 0;
    }

    validateManualRx() {
      const values = this.manualValues();
      const messages = [];
      const rxType = getCheckedValue(this.root, `lb-rx-type-${this.productId}`);

      if (!values.od_sph || !values.os_sph) {
        messages.push('SPH is required for both eyes.');
      }

      ['od', 'os'].forEach((eye) => {
        const cyl = Number(values[`${eye}_cyl`] || 0);
        if (cyl !== 0 && !values[`${eye}_axis`]) {
          messages.push(`${eye.toUpperCase()} Axis is required when CYL is not 0.00.`);
        }
      });

      if (rxType === 'progressives' && (!values.od_add || !values.os_add)) {
        messages.push('ADD is required for progressives.');
      }

      return messages;
    }

    manualValues() {
      const values = {};
      this.root.querySelectorAll('[data-lb-rx-field]').forEach((field) => {
        values[field.dataset.lbRxField] = field.value;
      });
      return values;
    }

    frameLabel(frameVariant) {
      const values = optionValuesForVariant(frameVariant);
      const frameOptions = this.productOptions
        .map((name, index) => index === this.lensOptionIndex ? null : values[index])
        .filter(Boolean)
        .join(' / ');

      return frameOptions ? `${this.product.title} - ${frameOptions}` : this.product.title;
    }

    commonProperties(frameVariant, lensVariant, prescriptionType, prescriptionMethod) {
      return {
        'Prescription Type': prescriptionType,
        'Prescription Method': prescriptionMethod,
        'Frame': this.frameLabel(frameVariant),
        'Lens': this.lensLabel(lensVariant)
      };
    }

    lensLabel(lensVariant) {
      if (!lensVariant) return '';
      if (lensVariant.magnificationType) return `${lensVariant.lens} - ${lensVariant.magnificationType}`;
      if (lensVariant.lensIndex) return `${lensVariant.lens} - ${lensVariant.lensIndex}`;
      return lensVariant.lens || '';
    }

    readerProperties(frameVariant, lensVariant) {
      return {
        ...this.commonProperties(frameVariant, lensVariant, 'Readers', 'Entered Manually'),
        'Reader Strength': prefixedPlus(getCheckedValue(this.root, `lb-reader-strength-${this.productId}`)),
        'Blue Light Filter': lensVariant.blueLightFilter,
        'Magnification Type': lensVariant.magnificationType
      };
    }

    prescriptionProperties(frameVariant, lensVariant) {
      const rxType = getCheckedValue(this.root, `lb-rx-type-${this.productId}`);
      const method = getCheckedValue(this.root, `lb-rx-method-${this.productId}`);
      const methodLabel = {
        upload: 'Upload File',
        manual: 'Entered Manually',
        emailLater: 'Send Later',
        onFile: 'Prescription On File'
      }[method] || 'Upload File';
      const prescriptionType = rxType === 'progressives' ? 'Progressives' : 'Single Vision';
      const props = {
        ...this.commonProperties(frameVariant, lensVariant, prescriptionType, methodLabel),
        'Blue Light Filter': lensVariant.blueLightFilter,
        'Lens Index': lensVariant.lensIndex
      };

      if (method === 'manual') {
        const values = this.manualValues();
        props['OD SPH'] = prefixedPlus(values.od_sph);
        props['OD CYL'] = prefixedPlus(values.od_cyl || 0);
        props['OD Axis'] = values.od_axis || '0';
        props['OS SPH'] = prefixedPlus(values.os_sph);
        props['OS CYL'] = prefixedPlus(values.os_cyl || 0);
        props['OS Axis'] = values.os_axis || '0';
        if (values.pd) props['PD'] = fixedTwo(values.pd);

        if (rxType === 'progressives') {
          props['OD ADD'] = prefixedPlus(values.od_add);
          props['OS ADD'] = prefixedPlus(values.os_add);
        }
      }

      const notes = normalize(this.root.querySelector('[data-lb-lens-notes]')?.value);
      if (notes) props['Lens Notes'] = notes;

      return props;
    }

    bundleProperties(productType, bundleId, prescriptionType, frameVariant, lensVariant) {
      const normalizedType = prescriptionType.replace(/\s+/g, '');
      const properties = {
        '_bundleEnabled': '1',
        '_lbBundleEnabled': '1',
        '_bundleId': bundleId,
        '_unique_identifier': `${normalizedType}__${this.productId}__${bundleId}`,
        '_productType': productType
      };

      if (productType === 'frame') {
        properties['_lens_variant_id'] = String(lensVariant.id);
      } else {
        properties['_base_variant_id'] = String(frameVariant.id);
      }

      return properties;
    }

    appendProperties(formData, itemIndex, properties, uploadFile) {
      Object.entries(properties).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          formData.append(`items[${itemIndex}][properties][${key}]`, value);
        }
      });

      if (uploadFile) {
        formData.append(`items[${itemIndex}][properties][Upload URL]`, uploadFile, uploadFile.name);
      }
    }

    async addConfiguredPair() {
      const addButtons = [...this.root.querySelectorAll('[data-lb-add], [data-lb-dock-add]')];
      if (addButtons.some((button) => button.getAttribute('aria-busy') === 'true')) return;
      if (!this.validate()) return;

      const frameVariant = this.currentFrameVariantForMode();
      const lensVariant = this.selectedLensVariant();
      const quantity = this.quantity();
      const bundleId = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const visibleProperties = this.mode === 'readers'
        ? this.readerProperties(frameVariant, lensVariant)
        : this.prescriptionProperties(frameVariant, lensVariant);
      const prescriptionType = visibleProperties['Prescription Type'];
      const uploadFile = this.mode === 'prescription' && getCheckedValue(this.root, `lb-rx-method-${this.productId}`) === 'upload'
        ? this.root.querySelector('[data-lb-upload-file]')?.files?.[0]
        : null;

      const frameProperties = {
        ...visibleProperties,
        ...this.bundleProperties('frame', bundleId, prescriptionType, frameVariant, lensVariant)
      };
      const lensProperties = {
        ...visibleProperties,
        ...this.bundleProperties('lens', bundleId, prescriptionType, frameVariant, lensVariant)
      };

      const formData = new FormData();
      formData.append('items[0][id]', frameVariant.id);
      formData.append('items[0][quantity]', quantity);
      this.appendProperties(formData, 0, frameProperties, uploadFile);
      formData.append('items[1][id]', lensVariant.id);
      formData.append('items[1][quantity]', quantity);
      this.appendProperties(formData, 1, lensProperties, uploadFile);

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));
      formData.append('sections', sectionsToBundle.join(','));
      formData.append('sections_url', `${Shopify.routes.root}variants/${frameVariant.id}`);
      formData.append('on_success', 'force_open_drawer');

      const status = this.root.querySelector('[data-lb-status]');
      addButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));
      this.updateSummaryDock();
      status.innerHTML = '';
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        const responseJson = await readJsonResponse(response, 'This pair could not be added.');

        if (!response.ok) {
          throw new Error(responseJson.description || 'This pair could not be added.');
        }

        const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
        const cartContent = await readJsonResponse(cartResponse, 'The pair was added, but the cart could not refresh.');
        cartContent.sections = responseJson.sections || {};

        this.form.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.items || [responseJson],
            cart: cartContent,
            onSuccessDo: 'force_open_drawer'
          }
        }));

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            onSuccessDo: 'force_open_drawer',
            cart: cartContent
          }
        }));

        this.showSuccess();
      } catch (error) {
        status.innerHTML = `<div class="banner banner--error" role="alert">${error.message}</div>`;
      } finally {
        addButtons.forEach((button) => button.removeAttribute('aria-busy'));
        this.updateSummaryDock();
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }

    showSuccess() {
      const status = this.root.querySelector('[data-lb-status]');
      const dock = this.root.querySelector('[data-lb-summary-dock]');
      if (dock) dock.hidden = true;
      status.innerHTML = `
        <div class="banner banner--success" role="alert">Pair added to cart.</div>
        <div class="lb-lens-builder__status-actions">
          <button type="button" class="button button--outline" data-lb-keep-shopping>KEEP SHOPPING</button>
          <a class="button" href="${Shopify.routes.root}cart">VIEW CART</a>
        </div>
      `;
      status.querySelector('[data-lb-keep-shopping]')?.addEventListener('click', () => {
        const drawer = document.getElementById('cart-drawer');
        if (typeof drawer?.hide === 'function') drawer.hide();
        status.innerHTML = '';
        this.updateSummaryDock();
      });
    }
  }

  class BundleQuantity {
    constructor(root) {
      this.root = root;
      this.pending = false;
      root.addEventListener('click', (event) => {
        const button = event.target.closest('[data-lb-bundle-action]');
        if (!button || this.pending) return;
        event.preventDefault();
        const action = button.dataset.lbBundleAction;
        const current = Number(this.input.value || 1);
        const target = action === 'remove' ? 0 : Math.max(1, current + (action === 'increase' ? 1 : -1));
        this.change(target);
      });
      this.input.addEventListener('change', () => {
        if (this.pending) return;
        this.change(Math.max(1, Number(this.input.value || 1)));
      });
    }

    get input() {
      return this.root.querySelector('[data-lb-bundle-quantity-input]');
    }

    async change(quantity) {
      const lineIndex = Number(this.root.dataset.lineIndex || 0);
      const frameKey = this.root.dataset.frameLineKey;
      const lensKey = this.root.dataset.lensLineKey;
      const isTransformedBundle = Number.isInteger(lineIndex) && lineIndex > 0;
      if ((!isTransformedBundle && (!frameKey || !lensKey)) || this.pending) return;

      this.pending = true;
      const controls = Array.from(this.root.querySelectorAll('button, input'));
      const disabledStates = new Map(controls.map((control) => [control, control.disabled]));
      controls.forEach((control) => {
        control.disabled = true;
      });

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const endpoint = isTransformedBundle
          ? `${Shopify.routes.root}cart/change.js`
          : `${Shopify.routes.root}cart/update.js`;
        const payload = isTransformedBundle
          ? {
              line: lineIndex,
              quantity,
              sections: sectionsToBundle.join(',')
            }
          : {
              updates: {
                [frameKey]: quantity,
                [lensKey]: quantity
              },
              sections: sectionsToBundle.join(',')
            };
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) throw new Error(`Cart update failed (${response.status})`);

        const cartContent = await response.json();

        if (window.themeVariables?.settings?.pageType === 'cart') {
          window.location.reload();
          return;
        }

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'line-item:change',
            cart: cartContent
          }
        }));
      } catch (error) {
        console.error('[LadyBoss] Could not update the paired cart items.', error);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        this.pending = false;
        controls.forEach((control) => {
          control.disabled = disabledStates.get(control);
        });
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }
  }

  function initLensBuilders(scope) {
    (scope || document).querySelectorAll(`${SELECTOR}:not([data-lb-initialized])`).forEach((root) => {
      root.dataset.lbInitialized = 'true';
      new LensBuilder(root);
    });
  }

  function initBundleQuantities(scope) {
    (scope || document).querySelectorAll('[data-lb-bundle-quantity]:not([data-lb-initialized])').forEach((root) => {
      root.dataset.lbInitialized = 'true';
      new BundleQuantity(root);
    });
  }

  function init(scope) {
    initLensBuilders(scope);
    initBundleQuantities(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('product:rerender', () => window.setTimeout(() => init(document), 0));
  document.addEventListener('cart-drawer:refreshed', (event) => init(event.target));

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) init(node);
      });
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
